import { useState, useEffect, useCallback } from "react";
import moment from "moment";
import { getValidToken } from "../../../utils/tokenManager";
import axios from "axios";
import { useContactContext } from "../../../contexts/ContactContext";

/**
 * Extracted from AgentDashborad.jsx (desktop) - same insights fetch,
 * same mood check-in submit, same "highest priority call type" logic.
 * Desktop now calls this hook too instead of keeping its own copy.
 */
export function useAgentInsights() {
  const { meetingRecovery, meetingContacts, followUpContacts, coldCallContacts } =
    useContactContext();

  const now = moment();
  const [period, setPeriod] = useState({ year: now.year(), month: now.month() + 1 });
  const [insights, setInsights] = useState(null);
  const [moodStatus, setMoodStatus] = useState({ already_submitted: false, mood_value: null });
  const [moodSubmitting, setMoodSubmitting] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [showEmpathy, setShowEmpathy] = useState(false);

  const getHighestPriorityCallType = () => {
    if (meetingRecovery.length > 0) return "meeting-recovery";
    if (meetingContacts.length > 0) return "meeting";
    if (followUpContacts.length > 0) return "follow-up";
    if (coldCallContacts.length > 0) return "cold-calls";
    return null;
  };

  const fetchInsights = useCallback(async (year, month) => {
    try {
      const accessToken = await getValidToken();
      const response = await axios({
        method: "get",
        url: `${process.env.REACT_APP_APIBASEURL}/api/agents/insights`,
        params: { year, month },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setInsights(response.data);
    } catch (error) {
      console.error("Error fetching agent insights:", error);
    }
  }, []);

  const fetchMoodStatus = useCallback(async () => {
    try {
      const accessToken = await getValidToken();
      const response = await axios({
        method: "get",
        url: `${process.env.REACT_APP_APIBASEURL}/api/agents/mood-checkin/today`,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setMoodStatus(response.data);
    } catch (error) {
      console.error("Error fetching mood status:", error);
    }
  }, []);

  useEffect(() => {
    fetchInsights(period.year, period.month);
  }, [period, fetchInsights]);

  useEffect(() => {
    fetchMoodStatus();
  }, [fetchMoodStatus]);

  const handleMonthChange = (delta) => {
    setPeriod((prev) => {
      const m = moment({ year: prev.year, month: prev.month - 1 }).add(delta, "month");
      return { year: m.year(), month: m.month() + 1 };
    });
  };

  const handleMoodClick = (value) => {
    if (moodStatus.already_submitted || moodSubmitting) return;

    setMoodStatus({ already_submitted: true, mood_value: value });
    setMoodSubmitting(true);
    if (value >= 3) {
      setCelebrate(true);
      setTimeout(() => setCelebrate(false), 2500);
    } else {
      setShowEmpathy(true);
      setTimeout(() => setShowEmpathy(false), 2500);
    }

    (async () => {
      try {
        const accessToken = await getValidToken();
        await axios({
          method: "post",
          url: `${process.env.REACT_APP_APIBASEURL}/api/agents/mood-checkin`,
          data: { MoodValue: value },
          headers: { Authorization: `Bearer ${accessToken}` },
        });
      } catch (error) {
        console.error("Error submitting mood check-in:", error);
        setMoodStatus({ already_submitted: false, mood_value: null });
      } finally {
        setMoodSubmitting(false);
      }
    })();
  };

  const kpisByDay = (insights?.kpis_by_day || []).map((d) => ({
    day: d.date.split("-")[2],
    realizadas: d.realizadas,
    contestadas: d.contestadas,
    interesados: d.interesados,
  }));

  const meetingsByDay = (insights?.meetings_by_day || []).map((d) => ({
    day: d.date.split("-")[2],
    agendadas: d.agendadas,
    efectivas: d.efectivas,
  }));

  const answered = insights?.answered_summary?.answered || 0;
  const notAnswered = insights?.answered_summary?.not_answered || 0;
  const answeredTotal = answered + notAnswered;
  const answeredPct = answeredTotal ? Math.round((answered / answeredTotal) * 100) : 0;
  const notAnsweredPct = answeredTotal ? 100 - answeredPct : 0;

  const ranking = insights?.ranking;
  const rankBarPct =
    ranking && ranking.my_position
      ? Math.round(((ranking.total_agents - ranking.my_position + 1) / ranking.total_agents) * 100)
      : 0;

  return {
    period,
    insights,
    moodStatus,
    moodSubmitting,
    celebrate,
    showEmpathy,
    getHighestPriorityCallType,
    handleMonthChange,
    handleMoodClick,
    kpisByDay,
    meetingsByDay,
    answered,
    notAnswered,
    answeredPct,
    notAnsweredPct,
    ranking,
    rankBarPct,
  };
}